import { useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import {
  cn,
  formatBytes,
  formatRelativeTime,
  getSupabase,
  useCurrentUser,
} from '@dogdisk/shared'
import {
  cacheFileList,
  getCachedFileList,
  type FileRow,
} from '../_lib/offline-cache'
import { MINE_FILE_COLS, fileRowFromMine, pinPackRoot, type MineFileRow } from '../_lib/file-query'
import { FileSectionToolbar } from './file-section-toolbar'
import { BatchActionBar } from './batch-action-bar'
import { SortFilterSheet } from './modals/sort-filter-sheet'
import { DEFAULT_SORT, sortFileRows, type SortState } from '../_lib/file-sort'
import type { MultiSelect } from '../_lib/use-multi-select'
import { FileActions } from './file-actions'
import { CreateMenu } from './create-menu'
import { FileThumb } from './file-thumb'
import { fileOpenHref } from '../_lib/file-route'

// 2026-05-29 mock 数据按 pen bi8Au 4 item + 补全 video/image/audio/file 全 icon 类型,
// 让未登录引导态能展示完整 5 类配色(folder / video / image / audio / 其他文件)
const MOCK_ROWS: FileRow[] = [
  { id: 'mock-folder-1', name: '工作文档', type: 'folder', status: 'active', parent_id: null, created_at: daysAgo(2) },
  { id: 'mock-folder-2', name: '照片', type: 'folder', status: 'active', parent_id: null, created_at: daysAgo(5) },
  { id: 'mock-video-1', name: 'vlog-旅行.mp4', type: 'video', size: 193_011_712, mime_type: 'video/mp4', status: 'active', parent_id: null, created_at: daysAgo(1) },
  { id: 'mock-image-1', name: 'sunset.jpg', type: 'image', size: 3_355_443, mime_type: 'image/jpeg', status: 'active', parent_id: null, created_at: daysAgo(0) },
  { id: 'mock-audio-1', name: '会议录音.m4a', type: 'audio', size: 12_582_912, mime_type: 'audio/mp4', status: 'active', parent_id: null, created_at: daysAgo(3) },
  { id: 'mock-file-1', name: '项目计划.pdf', type: 'pdf', size: 2_457_600, mime_type: 'application/pdf', status: 'active', parent_id: null, created_at: daysAgo(0) },
  { id: 'mock-file-2', name: '会议记录.docx', type: 'doc', size: 156_672, status: 'active', parent_id: null, created_at: daysAgo(0) },
  { id: 'mock-file-3', name: 'data-backup.zip', type: 'file', size: 524_288_000, mime_type: 'application/zip', status: 'active', parent_id: null, created_at: daysAgo(4) },
]

function daysAgo(d: number): string {
  return new Date(Date.now() - d * 86400_000).toISOString()
}

function useFileList(parentId: string | null, enabled: boolean) {
  const [placeholder, setPlaceholder] = useState<FileRow[] | undefined>(undefined)

  useEffect(() => {
    let cancelled = false
    void getCachedFileList(parentId).then((rows) => {
      if (!cancelled && rows) setPlaceholder(rows)
    })
    return () => {
      cancelled = true
    }
  }, [parentId])

  return useQuery({
    queryKey: ['file', 'list', parentId] as const,
    enabled,
    queryFn: async (): Promise<FileRow[]> => {
      const sb = getSupabase()
      // owner 收口走 v_file_info_mine(内建 user_id=auth.uid());view 已过滤 status='active'。
      // 直查 file_info 会因 file_info_select_visibility 宽 policy(is_public 默认 true)漏入别人的公开文件。
      let q = sb
        .from('v_file_info_mine')
        .select(MINE_FILE_COLS)
        .order('type', { ascending: false })
        .order('created_at', { ascending: false })
      q = parentId === null ? q.is('parent_id', null) : q.eq('parent_id', parentId)
      const { data, error } = await q
      if (error) {
        const cached = await getCachedFileList(parentId)
        if (cached) return cached
        throw error
      }
      const rows = ((data ?? []) as unknown as MineFileRow[]).map(fileRowFromMine)
      void cacheFileList(parentId, rows)
      return rows
    },
    ...(placeholder ? { placeholderData: placeholder } : {}),
  })
}

export function FileBrowser({ multiSelect }: { multiSelect: MultiSelect }) {
  const navigate = useNavigate()
  const { status } = useCurrentUser()
  const isAuth = status === 'authenticated'
  // 仅在明确未登录时显 mock 引导;loading 期间显 skeleton,避免 mock 数据闪现
  const isUnauth = status === 'unauthenticated'

  // 2026-05-29 拆独立路由:子文件夹现在走 /folders/:id(folder-detail.tsx 已实装);
  // 本组件只渲染根目录(parent_id=null),不再内部 crumbs state。
  const currentId: string | null = null
  const [actionFor, setActionFor] = useState<FileRow | null>(null)

  const [sort, setSort] = useState<SortState>(DEFAULT_SORT)
  const [sortOpen, setSortOpen] = useState(false)

  const { data, isLoading, refetch } = useFileList(currentId, isAuth)
  // rows 三元在 useMemo 内构造,避免每渲染新引用打穿 memo
  const sortedRows = useMemo(() => {
    const rows: FileRow[] = isAuth ? (data ?? []) : isUnauth ? MOCK_ROWS : []
    const sorted = sortFileRows(rows, sort)
    // 根目录(currentId===null)置顶「资源包」folder(subtype='pack_root');非根不置顶。
    return currentId === null ? pinPackRoot(sorted) : sorted
  }, [data, isAuth, isUnauth, sort, currentId])

  const ms = multiSelect
  // 上报当前可选 id 全集(全选用);列表变化(删除/移动后 refetch)同步
  useEffect(() => {
    ms.registerAll(sortedRows.map((r) => r.id))
    // ms.registerAll 由 useCallback(fn,[]) 封装,引用永远稳定
  }, [sortedRows, ms.registerAll])
  const selectedRows = sortedRows.filter((r) => ms.ids.has(r.id))

  return (
    <>
      {/* header(data-testid bi8Au)与 searchBar 已上移到 routes/files.tsx;
          子文件夹(crumbs)已拆独立路由 /folders/:id(folder-detail.tsx)。
          本组件只渲染:sectionHeader + list + FAB + sheets。*/}

      {/* .pen UUYuk sectionHeader — titleGroup + [多选, 排序](2026-06-07 改版) */}
      <FileSectionToolbar
        frameId="UUYuk"
        title="所有文件"
        count={sortedRows.length}
        onMultiSelect={() => { if (isAuth) multiSelect.enter() }}
        onSort={() => setSortOpen(true)}
      />

      {/* .pen SMaqT fileList — gap 12 / item: h 72 rounded 16 灰底 padding 16 gap 14 */}
      <ul className="flex flex-col gap-3 px-6 pb-safe-tab">
        {(isLoading && isAuth) || status === 'loading' ? (
          <SkeletonRows />
        ) : sortedRows.length === 0 ? (
          <li className="flex flex-col items-center gap-2 py-12 text-[13px] text-[#9CA3AF]">空空如也</li>
        ) : (
          sortedRows.map((f) => {
            const selected = ms.ids.has(f.id)
            return (
              <li
                key={f.id}
                data-testid={`files-row-${f.id}`}
                className={cn(
                  'flex items-center gap-[14px] rounded-2xl p-4',
                  ms.active && selected
                    ? 'bg-[#E3F7F2] ring-[1.5px] ring-inset ring-[#19caad]'
                    : 'bg-[#F6F7F8]',
                )}
              >
                {ms.active && (
                  <button
                    type="button"
                    role="checkbox"
                    aria-checked={selected}
                    data-testid="files-row-checkbox"
                    onClick={() => ms.toggle(f.id)}
                    className={cn(
                      'flex h-6 w-6 shrink-0 items-center justify-center rounded-xl',
                      selected ? 'bg-[#19caad]' : 'border-2 border-[#D1D5DB]',
                    )}
                  >
                    {selected && (
                      <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="#FFFFFF" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                        <path d="M20 6 9 17l-5-5" />
                      </svg>
                    )}
                  </button>
                )}
                <button
                  type="button"
                  data-testid="files-row-open-btn"
                  onClick={() => {
                    // 多选模式:行点击 = 切换选择
                    if (ms.active) {
                      ms.toggle(f.id)
                      return
                    }
                    // mock 引导态(未登录)下 row 点击 → 跳登录;真实数据(已登录)→ 走详情/预览
                    if (!isAuth) {
                      navigate('/auth/login')
                      return
                    }
                    // 按文件类型分流(folder→tC8FY / 媒体→zkY66·kENKt·qCOtS / 其他→pKcdM)
                    navigate(fileOpenHref(f))
                  }}
                  className="flex min-w-0 flex-1 items-center gap-[14px] text-left"
                >
                  <FileThumb row={f} />
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-[16px] font-semibold text-[#1A1A1A]">{f.name}</p>
                    <p className="mt-1 text-[12px] text-[#9CA3AF]">
                      {f.type === 'folder'
                        ? `文件夹 · ${formatRelativeTime(f.created_at)}`
                        : `${f.size ? formatBytes(f.size) : ''} · ${formatRelativeTime(f.created_at)}`}
                    </p>
                  </div>
                </button>
                {!ms.active && (
                  <button
                    type="button"
                    data-testid="files-row-more-btn"
                    aria-label="更多"
                    onClick={() => setActionFor(f)}
                    className="flex h-8 w-6 items-center justify-center text-[#9CA3AF]"
                  >
                    <svg width="4" height="16" viewBox="0 0 4 16" fill="currentColor">
                      <circle cx="2" cy="2" r="1.5" />
                      <circle cx="2" cy="8" r="1.5" />
                      <circle cx="2" cy="14" r="1.5" />
                    </svg>
                  </button>
                )}
              </li>
            )
          })
        )}
      </ul>

      {isAuth && !ms.active && <CreateMenu parentId={null} onChanged={() => void refetch()} />}

      {!isAuth && (
        <div
          className="fixed inset-x-0 z-20 mx-auto flex max-w-[402px] gap-2 px-6"
          style={{ bottom: 'calc(80px + env(safe-area-inset-bottom))' }}
        >
          <button
            type="button"
            data-testid="files-login-cta-btn"
            onClick={() => navigate('/auth/login')}
            className="h-11 flex-1 rounded-lg bg-[#19caad] text-[14px] font-semibold text-white shadow-lg"
          >
            登录后管理我的文件
          </button>
        </div>
      )}

      {actionFor && (
        <FileActions file={actionFor} parentId={currentId} onClose={() => setActionFor(null)} />
      )}

      <SortFilterSheet
        open={sortOpen}
        onClose={() => setSortOpen(false)}
        value={sort}
        onChange={setSort}
      />

      {ms.active && <BatchActionBar files={selectedRows} onDone={ms.exit} />}
    </>
  )
}

function SkeletonRows() {
  return (
    <>
      {[1, 2, 3, 4].map((i) => (
        <li key={i} className="flex items-center gap-3 rounded-xl bg-[#F6F7F8] px-3 py-3">
          <span className="skeleton h-11 w-11 rounded-lg" />
          <div className="flex-1 space-y-2">
            <span className="skeleton block h-3 w-3/5 rounded" />
            <span className="skeleton block h-2 w-2/5 rounded" />
          </div>
        </li>
      ))}
    </>
  )
}
